import { useState, useEffect } from 'react';
import { MapPin, Navigation, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { UserLocation } from '@/hooks/useUserLocation';

interface LocationChooserProps {
  currentLocation: UserLocation | null;
  onDetectLocation: () => void;
  onZipLookup: (zip: string) => void;
  onClearLocation: () => void;
  isLocating: boolean;
  error: string | null;
  radiusMiles: number;
  onRadiusChange: (radius: number) => void;
}

export function LocationChooser({
  currentLocation,
  onDetectLocation,
  onZipLookup,
  onClearLocation,
  isLocating,
  error,
  radiusMiles,
  onRadiusChange,
}: LocationChooserProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [zipInput, setZipInput] = useState('');

  // Auto-lookup when 5 digits entered
  useEffect(() => {
    if (/^\d{5}$/.test(zipInput)) {
      const timer = setTimeout(() => onZipLookup(zipInput), 500);
      return () => clearTimeout(timer);
    }
  }, [zipInput, onZipLookup]);

  return (
    <div
      className={`w-full mb-6 rounded-xl ${isAhp ? '' : 'border border-matrix-green/20 bg-black/40 backdrop-blur-sm'}`}
      style={isAhp ? {
        backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff',
        border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)',
        boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)',
      } : undefined}
    >
      {/* Single compact row on all widths: [📍 label ✕] [ZIP] [GPS] [60 mi ▾].
          Was flex-wrap, which broke into 3 stacked rows on mobile and wasted
          vertical space. Location label flex-shrinks/truncates; the controls
          stay fixed-width on the right and never wrap. */}
      <div className="px-3 py-2 flex items-center gap-2 flex-nowrap">
        {/* Location icon + label */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <MapPin
            className="w-4 h-4 shrink-0"
            style={isAhp ? { color: 'var(--civic-red)' } : { color: '#00ff00' }}
          />
          {currentLocation ? (
            <>
              <span
                className={`text-sm font-medium truncate ${isAhp ? '' : 'text-matrix-green'}`}
                style={isAhp ? { color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)' } : undefined}
              >
                {currentLocation.label}
              </span>
              <button
                onClick={onClearLocation}
                className="text-muted-foreground hover:text-foreground shrink-0"
                aria-label="Clear location"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <span className="text-sm text-muted-foreground truncate">Set your location</span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* ZIP input */}
          <Input
            type="text"
            inputMode="numeric"
            placeholder="ZIP"
            value={zipInput}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 5);
              setZipInput(val);
            }}
            className={`w-[64px] h-8 text-sm px-2 ${isAhp ? '' : 'bg-black/60 border-matrix-green/30 text-white placeholder:text-gray-500'}`}
            style={isAhp ? { borderColor: ahpDark ? 'rgba(var(--civic-gold-rgb),0.3)' : 'rgba(var(--civic-ink-rgb),0.2)' } : undefined}
          />

          {/* Use My Location — icon-only on mobile, label on sm+ */}
          <Button
            variant="outline"
            size="sm"
            onClick={onDetectLocation}
            disabled={isLocating}
            title="Use my location"
            aria-label="Use my location"
            className={`h-8 text-xs gap-1.5 px-2 sm:px-2.5 ${isAhp
              ? (ahpDark
                  // navy on the navy ground was invisible in dark mode
                  ? 'border-civic-navy-soft/40 text-civic-navy-soft hover:bg-civic-navy-soft/10'
                  : 'border-civic-navy/30 text-civic-navy hover:bg-civic-navy/5')
              : 'border-matrix-green/30 text-matrix-green hover:bg-matrix-green/10'
            }`}
          >
            {isLocating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Navigation className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">{isLocating ? 'Locating...' : 'Use My Location'}</span>
          </Button>

          {/* Radius dropdown */}
          <Select
            value={String(radiusMiles)}
            onValueChange={(v) => onRadiusChange(Number(v))}
          >
            <SelectTrigger
              className={`w-[74px] h-8 text-xs px-2 ${isAhp
                ? 'border-civic-ink/20'
                : 'bg-black/60 border-matrix-green/30 text-white'
              }`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 mi</SelectItem>
              <SelectItem value="60">60 mi</SelectItem>
              <SelectItem value="90">90 mi</SelectItem>
              <SelectItem value="120">120 mi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 pb-3 -mt-1">
          <p className="text-xs text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
}
